import {
  Box,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Skeleton,
  Text,
} from "@salestrak/ui";
import { SYSTEM_ROLE_ORDER } from "@salestrak/permissions";
import type { PermissionMatrixRole } from "#app/lib/data";

const COL_WIDTH = 140;
// The leading label column floors well above a role column: it carries a
// title + description pair, and as the only flex cell in the row it is the
// one that compresses when role columns multiply.
const ACTIONS_COL_MIN_WIDTH = 320;
// On a phone the card is ~330–345px wide, so the desktop floor left a role
// column a sliver to show in — the lane has to give the first value column
// its full width beside it, with the second peeking as the scroll hint.
// The value column keeps its width: it is sized by the "Only if created"
// checkbox row, which has no narrower form.
const ACTIONS_COL_MIN_WIDTH_PHONE = 180;
const ACTIONS_COL_MIN_WIDTH_RESPONSIVE = {
  base: ACTIONS_COL_MIN_WIDTH_PHONE,
  sm: ACTIONS_COL_MIN_WIDTH,
} as const;

// ─── Matrix chrome ────────────────────────────────────────────────────
// Both grant matrices are the same object: a leading label lane that stays
// put, and value columns at a set width that scroll under it inside the
// card. Rows carry title + wrapping description blocks, so their height is
// content-driven — which is why this is a composed grid and not the library
// `DataTable` (its virtualizer pins every row to one density height).

/** A row's floor: the leading lane's own floor plus every value column at
 *  its set width. Under this the card scrolls instead of squeezing the
 *  columns, which is what makes the lane's sticky offset meaningful — with
 *  no floor the columns would just compress and nothing would ever slide. */
export const matrixRowMinWidth = (columns: number) => ({
  base: ACTIONS_COL_MIN_WIDTH_PHONE + columns * COL_WIDTH,
  sm: ACTIONS_COL_MIN_WIDTH + columns * COL_WIDTH,
});

/** The matrix card. Only the x axis is named: height stays content-driven,
 *  so the y axis the browser is forced to pair with a clipped x has nothing
 *  to scroll and shows no second scrollbar. The rounded edge is what the
 *  columns disappear behind. */
export function MatrixCard({ children }: { children: React.ReactNode }) {
  return (
    <Box border rounded="lg" surface="card" overflowX="auto">
      {children}
    </Box>
  );
}

/** One matrix line, wide enough to hold every column at its set width.
 *  Opaque on purpose — the pinned lane inherits this fill, and a transparent
 *  row would let the columns scrolling beneath it show through.
 *
 *  No hover wash: with a pinned lane a row-level wash has nowhere good to go.
 *  The lane is a child of the row, so an alpha wash inherited into it paints
 *  a SECOND time over the row's own and the lane reads darker than the line
 *  it belongs to; painting the lane its resting fill instead leaves a seam at
 *  the lane's edge. The library `DataTable` solves this by replaying the wash
 *  as a layer over an opaque pinned cell — there is no row-level action here
 *  to justify importing that machinery, so the rows simply do not wash. */
export function MatrixRow({
  columns,
  background = "card",
  children,
}: {
  columns: number;
  background?: "card" | "muted";
  children: React.ReactNode;
}) {
  return (
    <Box
      background={background}
      direction="row"
      minWidth={matrixRowMinWidth(columns)}
    >
      {children}
    </Box>
  );
}

/** The pinned leading lane. It takes the row's OWN fill (`inherit`) rather
 *  than a named one, so the same part serves a `card` body row and a `muted`
 *  header band with nothing to plumb. Its right hairline is the lane's edge
 *  and stays with it while the columns slide — the value columns' own
 *  hairlines are between THEM, so the first one carries none. */
export function MatrixLead({
  gap,
  justify,
  paddingY = "md",
  children,
}: {
  gap?: "2xs";
  justify?: "center";
  paddingY?: "sm" | "md" | "lg";
  children: React.ReactNode;
}) {
  return (
    <Box
      background="inherit"
      border="right"
      fill="flex"
      gap={gap}
      justify={justify}
      left={0}
      minWidth={ACTIONS_COL_MIN_WIDTH_RESPONSIVE}
      paddingX="md"
      paddingY={paddingY}
      position="sticky"
      zIndex={1}
    >
      {children}
    </Box>
  );
}

/** One value column: a set width that never compresses, so the columns are
 *  the part that overflows. */
export function MatrixCell({
  first = false,
  paddingY = "md",
  children,
}: {
  first?: boolean;
  paddingY?: "sm" | "md";
  children?: React.ReactNode;
}) {
  return (
    <Box
      align="center"
      border={first ? undefined : "left"}
      gap="xs"
      justify="center"
      paddingX="md"
      paddingY={paddingY}
      shrink={false}
      width={COL_WIDTH}
    >
      {children}
    </Box>
  );
}

// First-load stand-in for either grant matrix: the real card chrome and
// muted header band render for real (they are static), with delayed bars
// where the role headers, action labels and toggles will land. Columns
// default to the system roster minus Owner — both settled matrices drop
// Owner (all-locked yes, it adds no decision). An extra custom column
// arriving is a far smaller jump than the old spinner→grid swap, and the
// delay means a warm cache never shows this at all.
export function PermissionsMatrixSkeleton({
  rows = 6,
  columns,
}: {
  rows?: number;
  columns?: number;
}) {
  const roleColumns =
    columns ?? SYSTEM_ROLE_ORDER.filter((r) => r !== "owner").length;
  return (
    <MatrixCard>
      <MatrixRow background="muted" columns={roleColumns}>
        <MatrixLead justify="center" paddingY="sm">
          <Skeleton delayed height={10} width={56} />
        </MatrixLead>
        {Array.from({ length: roleColumns }, (_, index) => (
          <MatrixCell key={index} first={index === 0} paddingY="sm">
            <Skeleton delayed height={10} width={64} />
          </MatrixCell>
        ))}
      </MatrixRow>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <MatrixRow key={rowIndex} columns={roleColumns}>
          <MatrixLead gap="2xs">
            {/* Capped so the description bar folds inside the phone lane. */}
            <Skeleton delayed height={12} width={160} />
            <Skeleton delayed height={10} width="min(240px, 100%)" />
          </MatrixLead>
          {Array.from({ length: roleColumns }, (_, colIndex) => (
            <MatrixCell key={colIndex} first={colIndex === 0}>
              <Skeleton delayed className="rounded-full" height={16} width={28} />
            </MatrixCell>
          ))}
        </MatrixRow>
      ))}
    </MatrixCard>
  );
}

// Shared column-header cell for both matrices. System roles render as
// plain text; custom roles get the chevron menu with Rename / Delete.
export function RoleColumnHeader({
  role,
  onRename,
  onDelete,
}: {
  role: PermissionMatrixRole;
  onRename: (role: { id: string; roleName: string }) => void;
  onDelete: (role: { id: string; roleName: string }) => void;
}) {
  if (role.isSystem) {
    return (
      <Text size="xs" variant="muted" weight="semibold">
        {role.roleName.toUpperCase()}
      </Text>
    );
  }
  return (
    <Menu>
      <MenuTrigger
        render={
          <Box
            as="button"
            direction="row"
            align="center"
            gap="2xs"
            rounded="sm"
            paddingX="2xs"
            hoverBackground
          />
        }
      >
        <Text size="xs" variant="muted" weight="semibold">
          {role.roleName.toUpperCase()}
        </Text>
        <Icon name="chevron-down" size="xs" color="muted" />
      </MenuTrigger>
      <MenuPopup align="end" className="min-w-40">
        <MenuItem onClick={() => onRename(role)}>
          <Icon name="pen-line" size="md" color="muted" />
          Rename role
        </MenuItem>
        <MenuItem onClick={() => onDelete(role)}>
          <Icon name="trash" size="md" />
          Delete role
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}
